import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { ArrowUp, ArrowDown, Check, Loader2, Smartphone } from "lucide-react";

const SURFACES = [
  { key: "home", label: "Floor Home tiles" },
  { key: "part", label: "Part card" },
  { key: "truck", label: "Truck card" },
];

// Owner-editable floor button set. Rename / reorder / show-hide from the FIXED catalog
// of wired actions (no create/delete — an owner can't invent a button with no backend).
export function FloorActionsTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState({}); // id -> draft label

  const load = async () => {
    const { data } = await api.get("/floor-actions", { params: { include_disabled: true } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); }, []);

  const bySurface = (s) => items.filter((x) => x.surface === s).sort((a, b) => a.sort - b.sort);

  const saveLabel = async (a) => {
    const label = (edit[a.id] ?? a.label).trim();
    if (!label || label === a.label) { setEdit((e) => ({ ...e, [a.id]: undefined })); return; }
    setBusy(true);
    try { await api.put(`/floor-actions/${a.id}`, { label }); setEdit((e) => ({ ...e, [a.id]: undefined })); await load(); toast.success("Saved"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = async (a, enabled) => {
    setBusy(true);
    try { await api.put(`/floor-actions/${a.id}`, { enabled }); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setTone = async (a, tone) => {
    setBusy(true);
    try { await api.put(`/floor-actions/${a.id}`, { tone }); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const move = async (surface, idx, dir) => {
    const list = bySurface(surface);
    const j = idx + dir;
    if (j < 0 || j >= list.length) return;
    const reordered = [...list];
    [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
    setBusy(true);
    try { await api.post("/floor-actions/reorder", { ids: reordered.map((x) => x.id) }); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="floor-actions-tab">
      <div className="flex items-start gap-2 text-sm text-slate-500">
        <Smartphone className="w-4 h-4 mt-0.5 shrink-0" />
        <p>Buttons and tiles shown on the Floor (tablet) surface. Rename them to match how your crew talks, reorder so the
          most-used sits nearest the thumb, and hide any you don’t need. These are the actions the app already
          knows how to do — nothing here can break a workflow. The <b>Scan / Look Up</b> tile is always on (it’s the
          floor’s entry point). Problem-alert tiles surface on their own when there’s a problem and aren’t listed here.</p>
      </div>
      {SURFACES.map((s) => (
        <div key={s.key} className="space-y-2">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-400">{s.label}</div>
          <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
            {bySurface(s.key).map((a, idx, arr) => (
              <div key={a.id} data-testid={`floor-action-row-${a.code}`} className="flex items-center gap-3 p-3">
                <div className="flex flex-col">
                  <button disabled={!canEdit || idx === 0 || busy} onClick={() => move(s.key, idx, -1)} className="disabled:opacity-30" data-testid={`floor-action-up-${a.code}`}><ArrowUp className="w-4 h-4" /></button>
                  <button disabled={!canEdit || idx === arr.length - 1 || busy} onClick={() => move(s.key, idx, 1)} className="disabled:opacity-30" data-testid={`floor-action-down-${a.code}`}><ArrowDown className="w-4 h-4" /></button>
                </div>
                <div className="flex-1 min-w-0">
                  {edit[a.id] !== undefined ? (
                    <div className="flex gap-2">
                      <Input autoFocus value={edit[a.id]} onChange={(e) => setEdit((x) => ({ ...x, [a.id]: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && saveLabel(a)} className="h-9" data-testid={`floor-action-input-${a.code}`} />
                      <Button size="sm" onClick={() => saveLabel(a)} disabled={busy} data-testid={`floor-action-save-${a.code}`}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                    </div>
                  ) : (
                    <button disabled={!canEdit} onClick={() => setEdit((x) => ({ ...x, [a.id]: a.label }))}
                      className="text-left font-semibold text-slate-900 hover:underline disabled:no-underline disabled:cursor-default" data-testid={`floor-action-label-${a.code}`}>
                      {a.label}
                    </button>
                  )}
                  <div className="font-mono text-[11px] text-slate-400">{a.code}</div>
                </div>
                <select disabled={!canEdit || busy} value={a.tone} onChange={(e) => setTone(a, e.target.value)}
                  className={`h-9 rounded-md border border-slate-300 text-sm px-2 bg-white ${s.key === "home" ? "invisible" : ""}`} data-testid={`floor-action-tone-${a.code}`}>
                  <option value="primary">Primary</option>
                  <option value="secondary">Secondary</option>
                </select>
                {a.code === "home_scan" ? (
                  <span className="text-[11px] font-semibold text-slate-400 whitespace-nowrap" data-testid="floor-action-scan-locked" title="The Scan tile is the floor's entry point and can't be hidden.">Always on</span>
                ) : (
                  <Switch checked={a.enabled} disabled={!canEdit || busy} onCheckedChange={(v) => toggle(a, v)} data-testid={`floor-action-enabled-${a.code}`} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
